import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ru, type TKey } from './ru';
import { uz } from './uz';
import {
  getLang,
  setCurrentLang,
  storeLang,
  type Lang,
  DEFAULT_LANG,
  LANGS,
} from './lang';

export type { Lang };
export { LANGS, DEFAULT_LANG };

const DICTS: Record<Lang, Record<TKey, string>> = { ru, uz };

export type Vars = Record<string, string | number>;
export type TFunction = (key: TKey, vars?: Vars) => string;

/** Подстановка `{name}` → значение. Отсутствующие переменные оставляем как есть. */
function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function translate(lang: Lang, key: TKey, vars?: Vars): string {
  // Фолбэк на русский нужен только если uz.ts когда-то разойдётся с ru.ts
  // в рантайме (например, после ручной правки собранного бандла).
  const template = DICTS[lang][key] ?? ru[key] ?? key;
  return interpolate(template, vars);
}

interface LangCtx {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
}

const LangContext = createContext<LangCtx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = getLang();
    setCurrentLang(initial); // проставляет <html lang="...">
    return initial;
  });

  const setLang = useCallback((next: Lang) => {
    // Сначала модульное значение — его читает lib/format.ts во время ререндера,
    // который вызовет setLangState ниже.
    setCurrentLang(next);
    storeLang(next);
    setLangState(next);
  }, []);

  const value = useMemo<LangCtx>(
    () => ({ lang, setLang, t: (key, vars) => translate(lang, key, vars) }),
    [lang, setLang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

function useLangContext(): LangCtx {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useT/useLang must be used inside <LangProvider>');
  return ctx;
}

/** Функция перевода. Меняется при смене языка → компонент перерисуется. */
export function useT(): TFunction {
  return useLangContext().t;
}

/** Текущий язык и сеттер — для переключателя. */
export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const { lang, setLang } = useLangContext();
  return { lang, setLang };
}
